// Configuration des plans de facturation Piktable
// Montants en CHF HT

export interface BillingPlan {
  name: string;
  basePrice: number;
  basePriceYearly: number; // Prix mensuel équivalent en mode annuel
  yearlyPrice: number; // Prix total annuel
  perReservation: number;
  maxMonthly: number;
  maxCommissions: number; // Plafond sur les commissions uniquement (mensuel)
  maxCommissionsYearly: number; // Plafond commissions en mode annuel (déduit de la part forfait)
  maxTotalYearly: number; // Total max mensuel en mode annuel
  aiCreditsPerMonth: number;
  badge: {
    label: string;
    color: 'blue' | 'gray' | 'green' | 'primary' | 'amber';
    showDaysLeft: boolean;
  };
}

export const BILLING_PLANS: Record<string, BillingPlan> = {
  trial: {
    name: 'Essai',
    basePrice: 0,
    basePriceYearly: 0,
    yearlyPrice: 0,
    perReservation: 0,
    maxMonthly: 0,
    maxCommissions: 0,
    maxCommissionsYearly: 0,
    maxTotalYearly: 0,
    aiCreditsPerMonth: 5,
    badge: { label: 'Essai', color: 'blue', showDaysLeft: true }
  },
  free: {
    name: 'Gratuit',
    basePrice: 0,
    basePriceYearly: 0,
    yearlyPrice: 0,
    perReservation: 0,
    maxMonthly: 0,
    maxCommissions: 0,
    maxCommissionsYearly: 0,
    maxTotalYearly: 0,
    aiCreditsPerMonth: 5,
    badge: { label: 'Gratuit', color: 'gray', showDaysLeft: false }
  },
  starter: {
    name: 'Starter',
    basePrice: 10,
    basePriceYearly: 8, // 96 CHF/an = 8 CHF/mois
    yearlyPrice: 96,
    perReservation: 0.5,
    maxMonthly: 99,
    maxCommissions: 89, // 99 - 10 = 89
    maxCommissionsYearly: 81, // 89 - 8 = 81 (8 CHF déjà payé dans le forfait annuel)
    maxTotalYearly: 89, // 8 + 81 = 89 CHF max/mois en annuel
    aiCreditsPerMonth: 5,
    badge: { label: 'Starter', color: 'green', showDaysLeft: false }
  },
  pro: {
    name: 'Pro',
    basePrice: 15,
    basePriceYearly: 12, // 144 CHF/an = 12 CHF/mois
    yearlyPrice: 144,
    perReservation: 1,
    maxMonthly: 99,
    maxCommissions: 84, // 99 - 15 = 84
    maxCommissionsYearly: 72, // 84 - 12 = 72 (12 CHF déjà payé dans le forfait annuel)
    maxTotalYearly: 84, // 12 + 72 = 84 CHF max/mois en annuel
    aiCreditsPerMonth: 5,
    badge: { label: 'Pro', color: 'primary', showDaysLeft: false }
  },
  premium: {
    name: 'Premium',
    basePrice: 35,
    basePriceYearly: 28, // 336 CHF/an = 28 CHF/mois
    yearlyPrice: 336,
    perReservation: 1,
    maxMonthly: 149,
    maxCommissions: 114, // 149 - 35 = 114
    maxCommissionsYearly: 86, // 114 - 28 = 86 (28 CHF déjà payé dans le forfait annuel)
    maxTotalYearly: 114, // 28 + 86 = 114 CHF max/mois en annuel
    aiCreditsPerMonth: 50,
    badge: { label: 'Premium', color: 'amber', showDaysLeft: false }
  }
};

// AI Credits helper functions
export function getAICreditsForPlan(planType: string): number {
  return BILLING_PLANS[planType]?.aiCreditsPerMonth ?? 5;
}

export interface BillingCalculation {
  planName: string;
  planType: string;
  baseAmount: number;
  reservationCount: number;
  rawReservationAmount: number; // Commissions avant plafond
  reservationAmount: number; // Commissions après plafond
  maxCommissions: number; // Plafond des commissions
  subtotal: number;
  maxMonthly: number;
  total: number;
  isCapped: boolean;
  isFree: boolean;
}

export function calculateBilling(planType: string, arrivedReservations: number): BillingCalculation {
  const plan = BILLING_PLANS[planType] || BILLING_PLANS.trial;
  
  const baseAmount = plan.basePrice;
  const rawReservationAmount = arrivedReservations * plan.perReservation;
  
  // Plafonner les commissions uniquement (pas le total)
  const reservationAmount = plan.maxCommissions > 0 
    ? Math.min(rawReservationAmount, plan.maxCommissions) 
    : rawReservationAmount;
  
  const isFree = plan.basePrice === 0 && plan.perReservation === 0;
  const maxMonthly = plan.maxMonthly;
  const maxCommissions = plan.maxCommissions;
  const total = baseAmount + reservationAmount;
  const isCapped = maxCommissions > 0 && rawReservationAmount > maxCommissions;
  
  return {
    planName: plan.name,
    planType,
    baseAmount,
    reservationCount: arrivedReservations,
    rawReservationAmount,
    reservationAmount,
    maxCommissions,
    subtotal: baseAmount + rawReservationAmount,
    maxMonthly,
    total,
    isCapped,
    isFree
  };
}

export function formatCHF(amount: number): string {
  return `${amount.toFixed(2)} CHF`;
}

export function getPlanLabel(planType: string): string {
  const plan = BILLING_PLANS[planType];
  if (!plan) return planType;
  
  if (plan.basePrice === 0 && plan.perReservation === 0) {
    return plan.name;
  }
  
  const perResaLabel = plan.perReservation === 1 
    ? '1 CHF/résa' 
    : `${plan.perReservation} CHF/résa`;
  
  return `${plan.name} (${plan.basePrice} CHF + ${perResaLabel})`;
}

export function isValidPlan(planType: string): boolean {
  return planType in BILLING_PLANS;
}

export function getAllPlanTypes(): string[] {
  return Object.keys(BILLING_PLANS);
}

export interface PlanBadgeInfo {
  label: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  className: string;
  tooltip: string;
  showIcon: boolean;
  iconType: 'clock' | 'warning' | 'sparkles' | 'crown' | null;
}

export function getPlanBadgeInfo(planType: string | null | undefined, trialEndsAt?: string | null): PlanBadgeInfo {
  const plan = BILLING_PLANS[planType || 'trial'];
  
  // Plans payants
  if (planType === 'starter') {
    return {
      label: 'Starter',
      variant: 'default',
      className: 'bg-green-500 hover:bg-green-600 text-white',
      tooltip: 'Plan Starter - 10 CHF + 0.50 CHF/réservation',
      showIcon: true,
      iconType: 'sparkles'
    };
  }
  
  if (planType === 'pro') {
    return {
      label: 'Pro',
      variant: 'default',
      className: 'bg-primary hover:bg-primary/90 text-primary-foreground',
      tooltip: 'Plan Pro - 15 CHF + 1 CHF/réservation',
      showIcon: true,
      iconType: 'sparkles'
    };
  }
  
  if (planType === 'premium') {
    return {
      label: 'Premium',
      variant: 'default',
      className: 'bg-amber-500 hover:bg-amber-600 text-white',
      tooltip: 'Plan Premium - 35 CHF + 1 CHF/réservation',
      showIcon: true,
      iconType: 'crown'
    };
  }
  
  if (planType === 'free') {
    return {
      label: 'Gratuit',
      variant: 'secondary',
      className: '',
      tooltip: 'Plan gratuit',
      showIcon: false,
      iconType: null
    };
  }
  
  // Plan trial (default)
  if (!trialEndsAt) {
    return {
      label: 'Essai',
      variant: 'outline',
      className: 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/30',
      tooltip: 'Période d\'essai',
      showIcon: true,
      iconType: 'clock'
    };
  }
  
  const now = new Date();
  const endDate = new Date(trialEndsAt);
  const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysLeft <= 0) {
    return {
      label: 'Essai expiré',
      variant: 'destructive',
      className: '',
      tooltip: `Essai expiré`,
      showIcon: true,
      iconType: 'warning'
    };
  }
  
  if (daysLeft <= 3) {
    return {
      label: `Essai - ${daysLeft}j`,
      variant: 'outline',
      className: 'border-amber-500 text-amber-600 bg-amber-50 dark:bg-amber-950/30',
      tooltip: `Fin de l'essai dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`,
      showIcon: true,
      iconType: 'warning'
    };
  }
  
  return {
    label: `Essai - ${daysLeft}j`,
    variant: 'outline',
    className: 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/30',
    tooltip: `${daysLeft} jours restants`,
    showIcon: true,
    iconType: 'clock'
  };
}
